<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <FancyServerTable
      v-if="isStateLoaded"
      ref="fancyTableRef"
      :data="orderTableData"
      :columns="orderTableHeaders"
      :total-rows="totalRows"
      :page-index="pageIndex"
      :page-size="pageSize"
      :enable-filters="true"
      :enable-pagination="true"
      :enable-column-visibility="true"
      :enable-column-pinning="true"
      :initial-column-pinning="
        currentSavedState?.columnPinning || {
          left: ['order_number'],
          right: ['actions'],
        }
      "
      :initial-column-visibility="currentSavedState?.columnVisibility || {}"
      :initial-column-order="currentSavedState?.columnOrder || []"
      :initial-column-sizing="currentSavedState?.columnSizing || {}"
      :initial-column-filters="currentSavedState?.columnFilters || []"
      :initial-sorting="currentSavedState?.sorting ?? []"
      :auto-filter="true"
      enable-row-selection
      :get-row-id="row => String(row.id)"
      @update:row-selection="handleRowSelectionChange"
      :loading="isLoading"
      :row-class-fn="rowClassFn"
      @update:state="debouncedHandleTableStateChange"
      @fetch-data="fetchOrders"
    >
      <template #actions>
        <OrdersTableBulkActions
          :selectedOrders="selectedOrders"
          v-if="selectedOrders.length >= 1"
          @reloadTable="reloadTable"
          @clearSelection="handleClearSelection"
        />
      </template>
      <template #config-manager>
        <FancyTableConfigManager
          :saved-configs="savedTableConfigs"
          :current-config="currentSavedState"
          :is-loading="false"
          :can-save-state="canSaveState"
          @save-config="handleSaveConfig"
          @update-config="handleUpdateConfig"
          @apply-config="handleApplyConfig"
          @delete-config="handleDeleteConfig"
          @mark-as-default="handleMarkAsDefault"
        />
      </template>
    </FancyServerTable>
  </div>
</template>

<script setup lang="ts">
import {
  FancyServerTable,
  FancyTableConfigManager,
  FieldType,
  FilterType,
  useTableRenderers,
  type FancyTableColumnDef,
  type ServerTableOptions,
} from '@/components/shared/fancyTables';
import { useOrderStore } from '@/stores/order.store';
import { h, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useFancyTableConfig } from '@/_helpers/composables/fancy-table-config.composable';
import OrdersTableBulkActions from './OrdersTableBulkActions.vue';
import OrdersTableRowActions from './OrdersTableRowActions.vue';
import { GetOrderNumberRenderer, GetStatusRenderer } from './Renderers';

const fancyTableRef = ref<any>(null);
const selectedOrders = ref<any[]>([]);
// Cache to store all selected row data across pages
const selectedRowsCache = ref<Map<string, any>>(new Map());
const { GetFieldRenderer } = useTableRenderers();

const { getOrders } = useOrderStore();
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    customerId: number;
    isDashboard: boolean;
  }>(),
  {
    isDashboard: false,
  }
);

// Server-side pagination state
const pageIndex = ref(0);
const pageSize = ref(10);
const totalRows = ref(0);

// Map TanStack Table filters to backend API parameters
const mapFiltersToApiParams = (filters: any[]) => {
  const params: any = {};

  filters.forEach(filter => {
    const { id, value } = filter;

    // Handle different filter types based on column configuration
    switch (id) {
      case 'order_number':
        params.order_number = value;
        break;
      case 'supplier_name':
        params.supplier_name = value;
        break;
      case 'document_no_internal':
        params.document_no_internal = value;
        break;
      case 'project_no':
        params.project_no = value;
        break;
      case 'balance':
        if (value?.start !== undefined) params.current_balance_min = value.start;
        if (value?.end !== undefined) params.current_balance_max = value.end;
        break;
      case 'amount':
        if (value?.start !== undefined) params.amount_min = value.start;
        if (value?.end !== undefined) params.amount_max = value.end;
        break;
      case 'service_date':
        if (value?.from) params.service_date_after = value.from;
        if (value?.to) params.service_date_before = value.to;
        break;
      case 'created_on':
        if (value?.from) params.created_on_after = value.from;
        if (value?.to) params.created_on_before = value.to;
        break;
      case 'approved_by_name':
        params.approved_by_name = value;
        break;
      case 'rejected_by_name':
        params.rejected_by_name = value;
        break;
      case 'cleared_by_names':
        params.cleared_by_name = value;
        break;
      case 'status_display':
        if (Array.isArray(value) && value.length > 0) {
          params.status = value.join(',');
        }
        break;
      case 'is_clarification_requested':
        if (value === true) params.clarification_requested_only = true;
        break;
      case 'fulfilled':
        if (value !== undefined) {
          // Explicitly convert boolean to string for Django BooleanFilter
          params.fulfilled = value ? 'true' : 'false';
        }
        break;
      case 'canceled':
        if (value !== undefined) {
          params.canceled = value ? 'true' : 'false';
        }
        break;
      case 'is_recurring':
        if (value !== undefined) {
          // Explicitly convert boolean to string for Django BooleanFilter
          params.is_recurring = value ? 'true' : 'false';
        }
        break;
      case 'expected_date_processing':
        if (value?.from) params.expected_date_processing_after = value.from;
        if (value?.to) params.expected_date_processing_before = value.to;
        break;
      case 'order_triggered':
        if (value !== undefined) {
          // Explicitly convert boolean to string for Django BooleanFilter
          params.order_triggered = value ? 'true' : 'false';
        }
        break;
      case 'order_triggered_on':
        if (value?.from) params.order_triggered_on_after = value.from;
        if (value?.to) params.order_triggered_on_before = value.to;
        break;
      case 'preliminary_insolvency_proceedings':
        if (value !== undefined) {
          // Explicitly convert boolean to string for Django BooleanFilter
          params.preliminary_insolvency_proceedings = value ? 'true' : 'false';
        }
        break;
      case 'order_no_supplier':
        params.order_no_supplier = value;
        break;
      case 'range_in_months':
        if (value?.start !== undefined) params.range_in_months_min = value.start;
        if (value?.end !== undefined) params.range_in_months_max = value.end;
        break;
    }
  });

  return params;
};

// Map TanStack Table sorting to backend API ordering parameter
const mapSortingToApiParam = (sorting: any[]): string => {
  if (sorting.length === 0) return '';

  const sort = sorting[0];
  const { id, desc } = sort;

  // Map frontend column IDs to backend field names
  let backendField = id;

  switch (id) {
    case 'order_number':
      // Sort by order number without prefix for proper numeric sorting
      backendField = 'order_number_without_prefix';
      break;
    case 'status_display':
      // Sort by the actual status field, not the display field
      backendField = 'status';
      break;
    case 'supplier_name':
      // Sort by the related supplier's name field
      backendField = 'supplier__name';
      break;
    case 'balance':
      // Sort by balance_sort which treats null values as 0 for consistent sorting
      backendField = 'balance_sort';
      break;
    case 'amount':
      // Sort by amount_sort which treats null values as 0 for consistent sorting
      backendField = 'amount_sort';
      break;
    case 'approved_by_name':
    case 'rejected_by_name':
      // Both derive from released_by; sort alphabetically by first name
      backendField = 'released_by__first_name';
      break;
    case 'project_no':
      // Sort by project_no_sort which pushes null/empty values to the end
      backendField = 'project_no_sort';
      break;
    case 'is_recurring':
      backendField = 'recurring_instruction_id';
      break;
    // Add more custom mappings here as needed
    default:
      backendField = id;
  }

  return desc ? `-${backendField}` : backendField;
};

// Fetch orders with server-side filtering, sorting, and pagination
const fetchOrders = async (options: ServerTableOptions) => {
  isLoading.value = true;

  try {
    // Map sorting
    const ordering = mapSortingToApiParam(options.sorting);

    // Map filters
    const filterParams = mapFiltersToApiParams(options.filters);

    const response = await getOrders({
      page: options.pageIndex + 1, // API uses 1-based indexing
      page_size: options.pageSize,
      ordering,
      customer: props.customerId,
      ...filterParams,
    });

    // Update state
    orderTableData.value = response.results.map((item: any) => ({ ...item }));
    totalRows.value = response.count;
    pageIndex.value = options.pageIndex;
    pageSize.value = options.pageSize;
  } catch (error) {
    console.error('Error fetching orders:', error);
    orderTableData.value = [];
    totalRows.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const rowClassFn = (row: { original: any }) =>
  row.original?.canceled ? 'tw-opacity-50 tw-line-through tw-text-neutral-400' : '';

// Reload table (used by bulk actions)
const reloadTable = () => {
  // Re-fetch with current settings
  fetchOrders({
    pageIndex: pageIndex.value,
    pageSize: pageSize.value,
    sorting: fancyTableRef.value?.getSerializedState()?.sorting || [],
    filters: fancyTableRef.value?.getSerializedState()?.columnFilters || [],
  });
};

const handleClearSelection = () => {
  fancyTableRef.value.resetRowSelection();
  selectedRowsCache.value.clear();
  selectedOrders.value = [];
};

// Initialize the table config composable
const {
  savedTableConfigs,
  currentSavedState,
  canSaveState,
  debouncedHandleTableStateChange,
  handleSaveConfig,
  handleApplyConfig,
  handleUpdateConfig,
  handleDeleteConfig,
  handleMarkAsDefault,
  isStateLoaded,
} = useFancyTableConfig({
  tableName: props.isDashboard ? 'orders_dashboard' : 'orders',
  fancyTableRef,
  tableLoader: async () => {
    // Load with initial state from saved config
    await fetchOrders({
      pageIndex: 0,
      pageSize: currentSavedState.value?.pageSize || 10,
      sorting: currentSavedState.value?.sorting || [{ id: 'order_number', desc: true }],
      filters: currentSavedState.value?.columnFilters || [],
    });
  },
});

const isLoading = ref(false);
const orderTableData = ref<any[]>([]);
const orderStatusOptions = [
  {
    label: t('orders.titles.clearing'),
    value: 'CLEARING',
    class:
      'tw-bg-purple-100 tw-border-purple-100 tw-text-purple-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
  {
    label: t('orders.titles.rejected'),
    value: 'REJECTED',
    class:
      'tw-bg-red-100 tw-border-red-100 tw-text-red-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
  {
    label: t('orders.titles.approved'),
    value: 'APPROVED',
    class:
      'tw-bg-green-100 tw-border-green-100 tw-text-green-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
  {
    label: t('orders.titles.pending'),
    value: 'PENDING_APPROVAL',
    class:
      'tw-bg-amber-100 tw-border-amber-100 tw-text-amber-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
  {
    label: t('orders.titles.draft'),
    value: 'DRAFT',
    class:
      'tw-bg-gray-100 tw-border-gray-100 tw-text-gray-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
];

const orderTableHeaders: FancyTableColumnDef<any>[] = [
  {
    header: t('orders.titles.order_no'),
    accessorKey: 'order_number',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: false,
    fieldType: FieldType.TEXT,
    cell: GetOrderNumberRenderer(),
    size: 100,
  },
  {
    header: t('orders.titles.supplier'),
    accessorKey: 'supplier_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    cell: info => {
      const name = info.getValue();
      if (!name) return '-';
      const isDeleted = info.row.original.supplier_is_deleted;
      if (isDeleted) {
        return h(
          'span',
          { class: 'tw-text-secondary-300 tw-cursor-default' },
          `${name} (${t('suppliers.labels.deleted')})`
        );
      }
      const supplierId = info.row.original.supplier;
      if (!supplierId) return name;
      return h(
        RouterLink,
        {
          class: 'tw-underline tw-text-secondary-500 hover:tw-text-primary-600 tw-cursor-pointer',
          to: { name: 'edit-supplier', params: { supplierId } },
        },
        () => name
      );
    },
    size: 150,
  },
  {
    header: t('orders.titles.doc_no_internal'),
    enableSorting: true,
    accessorKey: 'document_no_internal',
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 100,
  },
  {
    header: t('orders.titles.project_no'),
    accessorKey: 'project_no',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 100,
  },
  {
    header: t('orders.titles.current_balance'),
    accessorKey: 'balance',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    cell: GetFieldRenderer(FieldType.MONEY),
    filterFn: 'inNumberRange',
    sortingFn: 'money',
    size: 100,
  },
  {
    header: t('orders.titles.amount_gross'),
    accessorKey: 'amount',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    cell: GetFieldRenderer(FieldType.MONEY),
    filterFn: 'inNumberRange',
    sortingFn: 'money',
    size: 100,
  },
  {
    header: t('orders.titles.service_date'),
    accessorKey: 'service_date',
    enableSorting: true,
    enableColumnFilter: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 150,
  },
  {
    header: t('orders.titles.created_on'),
    accessorKey: 'created_on',
    enableSorting: true,
    enableColumnFilter: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 150,
  },
  {
    header: t('orders.titles.approved_by'),
    accessorKey: 'approved_by_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 150,
  },
  {
    header: t('orders.titles.rejected_by'),
    accessorKey: 'rejected_by_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 150,
  },
  {
    header: t('orders.titles.cleared_by'),
    accessorKey: 'cleared_by_names',
    enableSorting: false,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: (info: any) => {
      const names: string[] = info.getValue() ?? [];
      if (!names.length) return '-';
      const joined = names.join(', ');
      return h('span', { title: joined, class: 'tw-truncate tw-block tw-max-w-full' }, joined);
    },
    size: 200,
  },
  {
    header: t('orders.titles.status'),
    accessorKey: 'status_display',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BADGE,
    filterType: FilterType.MULTI_SELECT,
    filterFn: 'enumFilter',
    cell: GetStatusRenderer(orderStatusOptions),
    filterOptions: orderStatusOptions,
    size: 150,
  },
  {
    header: t('orders.titles.clarification_requested'),
    accessorKey: 'is_clarification_requested',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BOOLEAN,
    filterType: FilterType.BOOLEAN,
    filterFn: 'boolean',
    cell: GetFieldRenderer(FieldType.BOOLEAN),
    size: 150,
  },
  {
    header: t('orders.titles.fulfilled'),
    accessorKey: 'fulfilled',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BOOLEAN,
    filterType: FilterType.BOOLEAN,
    filterFn: 'boolean',
    cell: GetFieldRenderer(FieldType.BOOLEAN),
    size: 150,
  },
  {
    header: t('orders.titles.canceled'),
    accessorKey: 'canceled',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BOOLEAN,
    filterType: FilterType.BOOLEAN,
    filterFn: 'boolean',
    cell: GetFieldRenderer(FieldType.BOOLEAN),
    size: 150,
  },
  {
    header: t('orders.titles.is_recurring'),
    accessorKey: 'is_recurring',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BOOLEAN,
    filterType: FilterType.BOOLEAN,
    filterFn: 'boolean',
    cell: GetFieldRenderer(FieldType.BOOLEAN),
    size: 150,
  },
  {
    header: t('requests.titles.range_in_months'),
    accessorKey: 'range_in_months',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.NUMBER,
    filterType: FilterType.NUMBER_RANGE,
    cell: GetFieldRenderer(FieldType.NUMBER),
    filterFn: 'inNumberRange',
    size: 100,
  },
  {
    header: t('orders.titles.expected_processing_date'),
    accessorKey: 'expected_date_processing',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 150,
  },
  {
    header: t('orders.titles.order_triggered'),
    accessorKey: 'order_triggered',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BOOLEAN,
    filterType: FilterType.BOOLEAN,
    filterFn: 'boolean',
    cell: GetFieldRenderer(FieldType.BOOLEAN),
    size: 100,
  },
  {
    header: t('orders.titles.triggered_on'),
    accessorKey: 'order_triggered_on',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 150,
  },
  {
    header: t('orders.titles.prem_insv_proc'),
    accessorKey: 'preliminary_insolvency_proceedings',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BOOLEAN,
    filterType: FilterType.BOOLEAN,
    filterFn: 'boolean',
    cell: GetFieldRenderer(FieldType.BOOLEAN),
    size: 100,
  },
  {
    header: t('orders.titles.supplier_order_no'),
    accessorKey: 'order_no_supplier',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 100,
  },
  {
    header: t('common.headers.actions'),
    accessorKey: 'actions',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false,
    fieldType: FieldType.TEXT,
    cell: info =>
      h(OrdersTableRowActions, {
        key: `actions-${info.row.original.id}-${info.row.original.status_display}-${info.row.original.is_clarification_requested}-${info.row.original.fulfilled}-${info.row.original.canceled}`,
        order: info.row.original,
        onReloadTable: reloadTable,
      }),
    size: 50,
  },
];

const handleRowSelectionChange = (selection: any[]) => {
  // Update cache: add newly selected rows from current page
  selection.forEach(row => {
    selectedRowsCache.value.set(String(row.id), row);
  });

  // Remove deselected rows: find rows in current page that are no longer selected
  const currentPageIds = new Set(orderTableData.value.map(row => String(row.id)));
  const selectedIds = new Set(selection.map(row => String(row.id)));

  currentPageIds.forEach(id => {
    if (!selectedIds.has(id)) {
      selectedRowsCache.value.delete(id);
    }
  });

  // Update selectedOrders with all cached rows
  selectedOrders.value = Array.from(selectedRowsCache.value.values());
};
</script>
